/**
 * Hover — markdown card describing the symbol at the cursor.
 *
 * Card content varies by symbol kind:
 *   - module / surface:   typeName, qualifiedName, cascaded fields & aspects,
 *                       sub-counts (surfaces / interfaces / sub-modules),
 *                       reference count, defining file:line, description
 *   - interface:        typeName, reference count, description
 *   - process:          step count, callers (modules involved), description
 *   - view:             focus targets, group-by, layout, description
 *   - type:             parent kind chain, declared fields with markers
 *                       (required / cascade / append), aspects, who extends it
 */

import type { Hover, MarkupContent, Range } from "vscode-languageserver";
import type {
  Backlink,
  ClassifiedRef,
  AspectEntry,
  FieldDecl,
  MarkdownDocument,
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
} from "@archlang/engine";
import {
  classifyRef,
  forEachCallHop,
  forEachNode,
  nodeDescriptionContext,
  type DecorationInstance,
  REF_RE,
  renderDescription,
  slugify,
} from "@archlang/engine";
import type { CursorContext } from "../workspace/cursor-context.js";
import type { SymbolEntry, SymbolTable } from "../workspace/symbol-table.js";
import type { ReferenceIndex } from "../workspace/reference-index.js";
import type { Workspace } from "../workspace/workspace.js";
import { resolveSymbolAtCursor, offsetOf } from "../workspace/resolve.js";
import { toLspRange } from "../lsp-types.js";
import { countEdgeHover, maturationHint } from "./write-site-hints.js";

export function provideHover(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  refs: ReferenceIndex,
  uri: string,
  line: number,
  character: number
): Hover | null {
  // (c) Hover inside a `.md` document (§4.15): a `[[ … ]]` ref resolves to its
  // target; anywhere else shows the document card.
  const doc = workspace.getDocument(uri);
  if (doc) {
    const text = workspace.getDocumentText(uri) ?? "";
    const refHover = provideRefHover(workspace, symbols, refs, text, line, character, uri);
    if (refHover) return refHover;
    return provideDocumentHover(workspace, doc);
  }

  // (b) Hover inside a `[[ … ]]` ref embedded in an `.arch` description.
  const file = workspace.getFile(uri);
  if (file) {
    const refHover = provideRefHover(workspace, symbols, refs, file.text, line, character, uri);
    if (refHover) return refHover;
  }

  // (a2) Write-site hints (§4.13.2 / §8). The `count` head hint fully owns its
  // hover (the head token resolves to no symbol); the maturation note augments
  // the module card the callee resolves to.
  const offset = file ? offsetOf(file.text, line, character) : null;
  let maturation: ReturnType<typeof maturationHint> = null;
  if (file && offset !== null) {
    const countHover = countEdgeHover(workspace, uri, offset);
    if (countHover) return countHover;
    maturation = maturationHint(workspace, uri, offset);
  }

  const sym = resolveSymbolAtCursor(workspace, symbols, cursor, uri, line, character);
  if (!sym) {
    // A bare-module / trailing-dot callee whose module is an undeclared ghost
    // has no symbol card — surface the maturation note on its own.
    if (maturation) {
      return {
        contents: { kind: "markdown", value: maturation.note },
        range: toLspRange(maturation.span),
      };
    }
    return null;
  }

  const hit = cursor.qualifiedNameAt(uri, line, character);
  // Is the cursor on the symbol's own declaration, or on a use of it? The
  // declaration's body (fields, aspects, description) is already on screen and
  // the file:line is where the cursor sits — so at the definition we suppress
  // all of that and surface only the non-local facts. (LSP line is 0-based;
  // source spans are 1-based.)
  const defSpan = sym.nameSpan ?? sym.declSpan;
  const atDefinition =
    !!sym.definingUri &&
    uri === sym.definingUri &&
    !!defSpan &&
    line + 1 === defSpan.start.line;
  const markdown = renderHoverMarkdown(workspace, refs, symbols, sym, atDefinition);
  const value = maturation ? `${markdown}\n\n${maturation.note}` : markdown;
  const contents: MarkupContent = { kind: "markdown", value };
  const span = sym.nameSpan ?? sym.declSpan;
  return {
    contents,
    range: hit ? toLspRange(hit.span) : span ? toLspRange(span) : undefined,
  };
}

// =============================================================================
// (b) `[[ … ]]` reference hover — resolve the target under the cursor.
// =============================================================================

/**
 * If the cursor sits inside a `[[ … ]]` reference in `text`, return a hover
 * card describing the resolved target (element / aspect / document section).
 * Works the same for `.arch` descriptions and `.md` document bodies.
 */
function provideRefHover(
  workspace: Workspace,
  symbols: SymbolTable,
  refs: ReferenceIndex,
  text: string,
  line: number,
  character: number,
  fromUri?: string
): Hover | null {
  const offset = offsetOf(text, line, character);
  const span = refSpanAt(text, offset);
  if (!span) return null;
  const ref = classifyRef(span.inner);
  const markdown = renderRefHover(workspace, symbols, refs, ref, fromUri);
  if (!markdown) return null;
  return {
    contents: { kind: "markdown", value: markdown },
    range: offsetRange(text, span.start, span.end),
  };
}

/** The `[[ … ]]` span containing `offset`, if any. */
function refSpanAt(
  text: string,
  offset: number
): { start: number; end: number; inner: string } | null {
  REF_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REF_RE.exec(text)) !== null) {
    const start = m.index;
    const end = m.index + m[0].length;
    if (offset >= start && offset <= end) {
      return { start, end, inner: (m[1] ?? "").trim() };
    }
  }
  return null;
}

function renderRefHover(
  workspace: Workspace,
  symbols: SymbolTable,
  refs: ReferenceIndex,
  ref: ClassifiedRef,
  fromUri?: string
): string | null {
  const index = workspace.descriptionIndex();
  switch (ref.kind) {
    case "id": {
      const target = index.resolveId(ref.id);
      if (!target) return `**\`[[#${ref.id}]]\`** — *unresolved reference*`;
      return renderResolvedElement(workspace, refs, symbols, target.qualifiedName, `[[#${ref.id}]]`, fromUri);
    }
    case "name": {
      const cands = index.resolveName(ref.name);
      if (cands.length === 0) return `**\`[[${ref.name}]]\`** — *unresolved reference*`;
      if (cands.length > 1) {
        const list = cands.map((c) => `\`${c.qualifiedName}\``).sort().join(", ");
        return `**\`[[${ref.name}]]\`** — *ambiguous:* ${list}`;
      }
      return renderResolvedElement(workspace, refs, symbols, cands[0]!.qualifiedName, `[[${ref.name}]]`, fromUri);
    }
    case "aspect": {
      const present = index.resolveAspect(ref.key, ref.value);
      const label = ref.value !== undefined ? `${ref.key}: ${ref.value}` : ref.key;
      const status = present ? "" : " — *no node carries this aspect*";
      return `**aspect** \`${label}\`${status}`;
    }
    case "section": {
      const slugs = index.resolveDocument(ref.doc);
      if (slugs === undefined) {
        return `**\`[[${ref.doc}#${ref.section}]]\`** — *no such document*`;
      }
      const slug = slugify(ref.section);
      const ok = slugs.includes(slug);
      const status = ok ? "" : ` — *no section* \`#${slug}\``;
      return `**document** \`${ref.doc}\` *section* \`#${ref.section}\`${status}`;
    }
  }
}

/**
 * Render the hover card for an element a `[[ … ]]` ref resolves to. Reuses the
 * full symbol card when the target is in the symbol table; otherwise a compact
 * line.
 */
function renderResolvedElement(
  workspace: Workspace,
  refs: ReferenceIndex,
  symbols: SymbolTable,
  qualifiedName: string,
  rawRef: string,
  fromUri?: string
): string {
  // Referrer-aware (v0.13): a twin path resolves to the referrer's space.
  const sym = symbols.resolve(qualifiedName, fromUri);
  // A `[[ … ]]` reference is, by definition, a use-site: the target's body is
  // elsewhere, so render the full card (description + defined-at included).
  if (sym) return renderHoverMarkdown(workspace, refs, symbols, sym, false);
  return `**\`${rawRef}\`** → \`${qualifiedName}\``;
}

// =============================================================================
// (c) Markdown document hover — the document card.
// =============================================================================

function provideDocumentHover(
  workspace: Workspace,
  doc: MarkdownDocument
): Hover {
  const lines: string[] = [];
  lines.push(`**document** \`${doc.name}\``);
  if (doc.space && doc.space.length > 0) {
    lines.push(`*space:* \`${doc.space.join(".")}\``);
  }
  if (doc.sections.length > 0) {
    const rows = doc.sections.map(
      (s) => `${"  ".repeat(Math.max(0, s.level - 1))}- \`#${s.slug}\` ${s.text}`
    );
    lines.push("**Sections**");
    lines.push(rows.join("\n"));
  }
  // "Mentioned in" — other elements / documents that reference this document.
  const backlinks = workspace.backlinkIndex().forDocument(doc.name);
  pushBacklinkList(lines, backlinks);

  return { contents: { kind: "markdown", value: lines.join("\n\n") } };
}

// =============================================================================
// Offset → Range helpers.
// =============================================================================

function offsetRange(text: string, start: number, end: number): Range {
  return { start: positionAt(text, start), end: positionAt(text, end) };
}

function positionAt(text: string, offset: number): { line: number; character: number } {
  let line = 0;
  let character = 0;
  const limit = Math.min(offset, text.length);
  for (let i = 0; i < limit; i++) {
    if (text[i] === "\n") {
      line++;
      character = 0;
    } else {
      character++;
    }
  }
  return { line, character };
}

// =============================================================================
// Top-level renderer
// =============================================================================

/**
 * Render the hover card for a symbol. The card is deliberately *non-redundant*
 * with the editor: it never restates the body the cursor is sitting in.
 *
 *   `atDefinition` true  — cursor is on the declaration. The fields, aspects,
 *     description and source location are all on screen, so we show only the
 *     facts you cannot read locally: counts and cross-file usage.
 *   `atDefinition` false — cursor is on a *use* (a process step, a `[[ref]]`,
 *     another file). The body is off screen, so the description and a
 *     `defined at: file:line` jump target earn their place.
 */
function renderHoverMarkdown(
  workspace: Workspace,
  refs: ReferenceIndex,
  symbols: SymbolTable,
  sym: SymbolEntry,
  atDefinition: boolean
): string {
  const lines: string[] = [];
  const space = sym.definingUri
    ? workspace.model().spaces?.get(sym.definingUri)?.join(".")
    : undefined;
  pushHeader(lines, sym, space);

  // At a use-site the description is the most useful thing the reader can't
  // see — surface it right under the header.
  if (!atDefinition) pushDescription(lines, workspace, sym);

  switch (sym.kind) {
    case "module":
    case "surface":
      pushModuleOrSurfaceBody(lines, workspace, sym);
      break;
    case "process":
      pushProcessBody(lines, workspace, sym);
      break;
    case "subprocess":
      pushSubprocessBody(lines, workspace, sym);
      break;
    case "view":
      pushViewBody(lines, workspace, sym);
      break;
    case "type":
      pushTypeBody(lines, workspace, symbols, sym);
      break;
  }

  pushReferenceCount(lines, refs, sym);
  pushBacklinks(lines, workspace, sym);
  // The location is only worth showing when the cursor is NOT already there.
  if (!atDefinition) pushDefinedAt(lines, sym);

  return lines.join("\n\n");
}

/**
 * "Mentioned in" (§4.10 / §4.15) — descriptions + `.md` documents that
 * reference this element via `[[Name]]`, `[[Owner.Name]]`, or `[[#id]]`.
 */
function pushBacklinks(
  lines: string[],
  workspace: Workspace,
  sym: SymbolEntry
): void {
  const idx = workspace.backlinkIndex();
  const seen = new Set<string>();
  const links: Backlink[] = [];
  const add = (b: Backlink): void => {
    const key = `${b.kind}|${b.qualifiedName ?? ""}|${b.path ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    links.push(b);
  };
  for (const b of idx.forName(sym.name)) add(b);
  if (sym.qualifiedName !== sym.name) {
    for (const b of idx.forName(sym.qualifiedName)) add(b);
  }
  // Stable-id refs (`[[#m1]]`) only key on a real id, never the synthesized
  // `module:Foo` fallbacks, so this is a no-op for id-less symbols.
  for (const b of idx.forId(sym.id)) add(b);
  pushBacklinkList(lines, links);
}

/** Render a "Mentioned in" list, or nothing when there are no backlinks. */
function pushBacklinkList(lines: string[], links: readonly Backlink[]): void {
  if (links.length === 0) return;
  const rows = links.map((b) =>
    b.kind === "document"
      ? `- \`${b.name}\` *(document)*`
      : `- \`${b.qualifiedName ?? b.name}\``
  );
  lines.push("**Mentioned in**");
  lines.push(rows.join("\n"));
}

// =============================================================================
// Sections
// =============================================================================

function pushHeader(lines: string[], sym: SymbolEntry, space?: string): void {
  // A re-entry label (PROCESS.md §7) has an ugly synthesized qualifiedName
  // (`reentry:proc:Owner.Name:label`) — show the bare label + its process.
  if (sym.kind === "reentry") {
    // qualifiedName is `reentry:<proc|subproc>:<qname>:<label>`.
    const procQn = sym.qualifiedName.split(":")[2] ?? "";
    lines.push(`**re-entry label** \`${sym.name}\``);
    if (procQn) lines.push(`*in process:* \`${procQn}\``);
    return;
  }
  const typeLabel = sym.typeName
    ? `${sym.kind} \`${sym.typeName}\``
    : sym.kind;
  lines.push(`**${typeLabel}** \`${sym.qualifiedName}\``);
  // Enclosing space (v0.5), when the symbol lives in one.
  if (space) lines.push(`*space:* \`${space}\``);
}

function pushModuleOrSurfaceBody(
  lines: string[],
  workspace: Workspace,
  sym: SymbolEntry
): void {
  const node = findModuleOrSurface(workspace.model(), sym.qualifiedName);
  if (!node) return;
  const isSurface = sym.kind === "surface";

  // A glanceable tally — never the body itself. The fields and aspects are
  // written right there in the source, and the full data is still available to
  // MCP and other model consumers; the editor reader only needs the shape.
  const parts: string[] = [];
  const fields = node.body.fields.filter((f) => f.value !== undefined).length;
  if (fields > 0) parts.push(count(fields, "field"));
  const aspects = node.body.aspects.filter((l) => l.value !== undefined).length;
  if (aspects > 0) parts.push(count(aspects, "aspect"));
  if (node.surfaces.length > 0) parts.push(count(node.surfaces.length, "surface"));
  if (node.interfaces.length > 0) parts.push(count(node.interfaces.length, "interface"));
  if (!isSurface && "modules" in node && (node as ResolvedModule).modules.length > 0) {
    parts.push(count((node as ResolvedModule).modules.length, "sub-module"));
  }
  if (parts.length > 0) lines.push(`*${parts.join(" · ")}*`);
}

/** `3 → "3 interfaces"`, `1 → "1 interface"`. */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

function pushProcessBody(
  lines: string[],
  workspace: Workspace,
  sym: SymbolEntry
): void {
  const proc = workspace.model().processes.find((p) => {
    const qn = p.ownerPath && p.ownerPath.length > 0
      ? `${p.ownerPath.join(".")}.${p.name}`
      : p.name;
    return qn === sym.qualifiedName;
  }) ?? workspace.model().processes.find((p) => p.name === sym.name);
  if (!proc) return;
  const stepCount = proc.steps.length;
  if (stepCount > 0) {
    lines.push(`*steps:* ${stepCount}`);
  }
  // Caller modules — the RESOLVED caller of every hop (§4.13.4), so an omitted
  // head, a `|` fan and a `\` snake all name the module that performs the call.
  // Nested bodies (branches, saga items, unwinds) come free with the IR walk.
  const callers = new Set<string>();
  const body = workspace.model().processModels?.find(
    (pm) => pm.source && proc.source
      && pm.source.file === proc.source.file
      && pm.source.start.offset === proc.source.start.offset
  )?.body;
  forEachCallHop(body, (hop) => {
    if (hop.caller.length > 0) callers.add(hop.caller.join("."));
  });
  forEachNode(body, (n) => {
    if (n.kind === "note" && n.owner.resolved && n.owner.resolved.length > 0) {
      callers.add(n.owner.resolved.join("."));
    }
  });
  if (callers.size > 0) {
    const list = [...callers].sort().map((c) => `\`${c}\``).join(", ");
    lines.push(`*invoked from:* ${list}`);
  }
}

function pushSubprocessBody(
  lines: string[],
  workspace: Workspace,
  sym: SymbolEntry
): void {
  const sp = workspace.model().subprocesses.find((s) => {
    const qn = s.ownerPath && s.ownerPath.length > 0
      ? `${s.ownerPath.join(".")}.${s.name}`
      : s.name;
    return qn === sym.qualifiedName;
  }) ?? workspace.model().subprocesses.find((s) => s.name === sym.name);
  if (!sp) return;
  if (sp.params.length > 0) {
    lines.push(`*params:* ${sp.params.map((p) => `\`${p}\``).join(", ")}`);
  }
  const stepCount = sp.steps.length;
  if (stepCount > 0) {
    lines.push(`*steps:* ${stepCount}`);
  }
}

/** Canonical clause order + display labels for the hover summary. */
const VIEW_CLAUSE_LABELS: readonly (readonly [string, string])[] = [
  ["show", "show"],
  ["hide", "hide"],
  ["focus", "focus"],
  ["groupBy", "group by"],
  ["style", "style"],
  ["on", "on"],
  ["flow", "flow"],
  ["table", "table"],
  ["matrix", "matrix"],
  ["grid", "grid"],
  ["lens", "lens"],
  ["story", "story"],
];

function pushViewBody(
  lines: string[],
  workspace: Workspace,
  sym: SymbolEntry
): void {
  const v = workspace.model().views.find((view) => view.name === sym.name);
  if (!v) return;
  if (v.instanceOf) lines.push(`*instance of:* \`${v.instanceOf}\``);
  // Clause summary: `<n> <label>` per clause kind present, canonical order.
  const counts = new Map<string, number>();
  for (const c of v.clauses) counts.set(c.kind, (counts.get(c.kind) ?? 0) + 1);
  const parts = VIEW_CLAUSE_LABELS.filter(([kind]) => counts.has(kind)).map(
    ([kind, label]) => `${counts.get(kind)} ${label}`
  );
  if (parts.length > 0) lines.push(`*clauses:* ${parts.join(", ")}`);
  if (v.header.knobs.length > 0) lines.push(`*${count(v.header.knobs.length, "knob")}*`);
}

function pushTypeBody(
  lines: string[],
  workspace: Workspace,
  symbols: SymbolTable,
  sym: SymbolEntry
): void {
  const reg = workspace.model().registry.kinds;
  const kind = reg.get(sym.name);
  if (!kind) return;

  if (kind.chain.length > 0) {
    lines.push(`*extends:* ${kind.chain.map((c) => `\`${c}\``).join(" → ")}`);
  }
  lines.push(`*base type:* \`${kind.baseType}\``);

  // Stamped fields: a count, not the contract listing (the body is on screen).
  const stamped = kind.stampedBody.fields.length;
  if (stamped > 0) lines.push(`*${count(stamped, "field")}*`);

  // Who extends this type
  const extenders = [...reg.entries()]
    .filter(([n, k]) => n !== sym.name && k.chain.includes(sym.name))
    .map(([n]) => `\`${n}\``);
  if (extenders.length > 0) {
    lines.push(`*extended by:* ${extenders.join(", ")}`);
  }

  // Instance count: how many modules / surfaces / interfaces use this kind.
  const instanceCount = symbols
    .all()
    .filter((s) => s.typeName === sym.name && s.kind !== "type")
    .length;
  if (instanceCount > 0) {
    lines.push(`*instances:* ${instanceCount}`);
  }
}

function pushReferenceCount(
  lines: string[],
  refs: ReferenceIndex,
  sym: SymbolEntry
): void {
  const n = refs.referencesTo(sym.id).length;
  if (n > 0) lines.push(`*references:* ${n}`);
}

function pushDefinedAt(lines: string[], sym: SymbolEntry): void {
  if (!sym.definingUri) return;
  const span = sym.nameSpan ?? sym.declSpan;
  if (!span) return;
  // Render as `<basename>:<line>` for compactness.
  const base = sym.definingUri.split("/").pop() ?? sym.definingUri;
  lines.push(`*defined at:* \`${base}:${span.start.line}\``);
}

function pushDescription(
  lines: string[],
  workspace: Workspace,
  sym: SymbolEntry
): void {
  if (!sym.description) return;
  const ctx = nodeDescriptionContext(workspace.descriptionIndex(), {
    aspects: effectiveAspectsForSymbol(workspace, sym),
    fields: effectiveFieldsForSymbol(workspace, sym),
    decorations: effectiveDecorationsForSymbol(workspace, sym),
  });
  const { markdown } = renderDescription(sym.description, ctx);
  lines.push("---");
  lines.push(markdown);
}

/**
 * Look up the aspects visible on the owning node of `sym`. For modules
 * this is the cascaded `body.aspects`; for surfaces/interfaces it is the
 * type-stamped `body.aspects` (cascade does not propagate into them).
 * Returns an empty array for symbols that have no body (e.g. processes,
 * views — covered separately by their own resolvers if needed later).
 */
function effectiveAspectsForSymbol(
  workspace: Workspace,
  sym: SymbolEntry
): readonly AspectEntry[] {
  const model = workspace.cascadedModel();
  switch (sym.kind) {
    case "module": {
      const node = findModuleByQualified(model, sym.qualifiedName);
      return node?.body.aspects ?? [];
    }
    case "surface": {
      const node = findSurfaceByQualified(model, sym.qualifiedName);
      return node?.body.aspects ?? [];
    }
    case "interface": {
      const node = findInterfaceByQualified(model, sym.qualifiedName);
      return node?.body.aspects ?? [];
    }
    default:
      return [];
  }
}

/** Fields visible on the owning node of `sym` — the value-axis sibling of
 *  `effectiveAspectsForSymbol`, backing `@field` interpolation in hover. */
function effectiveFieldsForSymbol(
  workspace: Workspace,
  sym: SymbolEntry
): readonly FieldDecl[] {
  const model = workspace.cascadedModel();
  switch (sym.kind) {
    case "module":
      return findModuleByQualified(model, sym.qualifiedName)?.body.fields ?? [];
    case "surface":
      return findSurfaceByQualified(model, sym.qualifiedName)?.body.fields ?? [];
    case "interface":
      return findInterfaceByQualified(model, sym.qualifiedName)?.body.fields ?? [];
    default:
      return [];
  }
}

/** The decorations on the owning node — the third leg of the `@` axis, looked
 *  up exactly like its aspects and fields. Modules/surfaces/interfaces carry
 *  them on `.decl`; anything else has none. */
function effectiveDecorationsForSymbol(
  workspace: Workspace,
  sym: SymbolEntry
): readonly DecorationInstance[] | undefined {
  const model = workspace.cascadedModel();
  switch (sym.kind) {
    case "module":
      return findModuleByQualified(model, sym.qualifiedName)?.decl.decorations;
    case "surface":
      return findSurfaceByQualified(model, sym.qualifiedName)?.decl.decorations;
    case "interface":
      return findInterfaceByQualified(model, sym.qualifiedName)?.decl.decorations;
    default:
      return undefined;
  }
}

function findModuleByQualified(
  model: ResolvedModel,
  qname: string
): ResolvedModule | null {
  const parts = qname.split(".");
  let modules: readonly ResolvedModule[] = model.modules;
  let current: ResolvedModule | null = null;
  for (const p of parts) {
    const found = modules.find((m) => m.name === p);
    if (!found) return null;
    current = found;
    modules = found.modules;
  }
  return current;
}

function findSurfaceByQualified(
  model: ResolvedModel,
  qname: string
): ResolvedSurface | null {
  const parts = qname.split(".");
  if (parts.length < 2) return null;
  const owner = findModuleByQualified(model, parts.slice(0, -1).join("."));
  if (!owner) return null;
  const tail = parts[parts.length - 1]!;
  return findSurfaceIn(owner.surfaces, tail);
}

function findInterfaceByQualified(
  model: ResolvedModel,
  qname: string
): ResolvedInterface | null {
  const parts = qname.split(".");
  if (parts.length < 2) return null;
  const tail = parts[parts.length - 1]!;
  const ownerPath = parts.slice(0, -1);
  const owner = findOwner(model, ownerPath);
  if (!owner) return null;
  return owner.interfaces.find((i) => i.name === tail) ?? null;
}

function findOwner(
  model: ResolvedModel,
  path: readonly string[]
): ResolvedModule | ResolvedSurface | null {
  let modules: readonly ResolvedModule[] = model.modules;
  let surfaces: readonly ResolvedSurface[] = [];
  let current: ResolvedModule | ResolvedSurface | null = null;
  for (const p of path) {
    const inMods = modules.find((m) => m.name === p);
    if (inMods) {
      current = inMods;
      modules = inMods.modules;
      surfaces = inMods.surfaces;
      continue;
    }
    const inSurfaces = surfaces.find((f) => f.name === p);
    if (inSurfaces) {
      current = inSurfaces;
      modules = [];
      surfaces = inSurfaces.surfaces;
      continue;
    }
    return null;
  }
  return current;
}

function findSurfaceIn(
  surfaces: readonly ResolvedSurface[],
  name: string
): ResolvedSurface | null {
  for (const f of surfaces) {
    if (f.name === name) return f;
    const nested = findSurfaceIn(f.surfaces, name);
    if (nested) return nested;
  }
  return null;
}

// =============================================================================
// Helpers
// =============================================================================

function findModuleOrSurface(
  model: ResolvedModel,
  qualifiedName: string
): ResolvedModule | ResolvedSurface | null {
  const parts = qualifiedName.split(".");
  let nodes: readonly (ResolvedModule | ResolvedSurface)[] = model.modules;
  let current: ResolvedModule | ResolvedSurface | null = null;
  for (const part of parts) {
    const found: ResolvedModule | ResolvedSurface | undefined = nodes.find(
      (n) => n.name === part
    );
    if (!found) return null;
    current = found;
    const subModules = "modules" in found ? found.modules : [];
    nodes = [...subModules, ...found.surfaces];
  }
  return current;
}
